import { useState, useEffect } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Card, CardMedia, TextField } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PyrenzBlueButton } from '@client/theme'

interface StylePreset {
  preset_prompt?: string
  model: string
  steps: number
  width: number
  height: number
  cfg_scale: number
  karras: boolean
  sampler_name: string
}

interface PreviewImages {
  person?: string
  place?: string
  thing?: string
}

interface StylePresetSelectorProps {
  onSelect: (key: string, preset: StylePreset) => void
}

export function StylePresetSelector({ onSelect }: StylePresetSelectorProps) {
  const [styles, setStyles] = useState<Record<string, StylePreset>>({})
  const [previews, setPreviews] = useState<Record<string, PreviewImages>>({})
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const pageSize = 25

  useEffect(() => {
    fetch('https://raw.githubusercontent.com/Haidra-Org/AI-Horde-Styles/main/styles.json')
      .then(r => r.json())
      .then(d => {
        setStyles(d)
        const defaultPreset = d['noob-v-pencil']
        if (defaultPreset && !selectedKey) {
          setSelectedKey('noob-v-pencil')
          onSelect('noob-v-pencil', defaultPreset)
        }
      })
      .catch(console.error)

    fetch('https://raw.githubusercontent.com/amiantos/AI-Horde-Styles-Previews/main/previews.json')
      .then(r => r.json())
      .then(d => setPreviews(d))
      .catch(console.error)
  }, [])

  const filteredKeys = Object.keys(styles).filter(key => key.toLowerCase().includes(search.toLowerCase()))
  const totalPages = Math.ceil(filteredKeys.length / pageSize)
  const visibleKeys = filteredKeys.slice(page * pageSize, page * pageSize + pageSize)

  const applyPreset = (key: string) => {
    const preset = styles[key]
    if (!preset) return
    setSelectedKey(key)
    onSelect(key, preset)
  }

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Style Presets</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search styles…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {visibleKeys.map(key => {
            const previewUrl = previews[key]?.person
            const isSelected = key === selectedKey
            return (
              <Box
                key={key}
                sx={{
                  width: { xs: '48%', sm: '32%', md: '23%' },
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease',
                  border: isSelected ? '2px solid #4caf50' : '2px solid transparent',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'scale(1.04)',
                    boxShadow: '0 4px 18px rgba(0,0,0,0.25)'
                  }
                }}
                onClick={() => applyPreset(key)}
              >
                <Card sx={{ p: 1, borderRadius: 1 }}>
                  {previewUrl ? (
                    <CardMedia
                      component="img"
                      height={100}
                      image={previewUrl}
                      alt={key}
                      loading="lazy"
                      sx={{ objectFit: 'cover', borderRadius: 1, transition: 'opacity 0.2s ease', '&:hover': { opacity: 0.9 } }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f0f0f0',
                        borderRadius: 1,
                        color: '#999',
                        fontSize: 12
                      }}
                    >
                      No preview
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-word' }}>{key}</Typography>
                </Card>
              </Box>
            )
          })}
        </Box>

        {filteredKeys.length > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <PyrenzBlueButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</PyrenzBlueButton>
            <Typography sx={{ alignSelf: 'center' }}>Page {page + 1} / {totalPages}</Typography>
            <PyrenzBlueButton disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</PyrenzBlueButton>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}
